import type { WhapiChannel } from "@/services/types/whapi";

const WHATSAPP_RECONNECT_DISMISS_PREFIX = "openfarming.waReconnect.dismissed.";

type WhatsAppConnectedStatus = Extract<
  WhapiChannel["auth_status"],
  "AUTH" | "AUTHENTICATED"
>;

export type WhatsAppDisconnectedStatus = Extract<
  WhapiChannel["auth_status"],
  "QR_FETCHED" | "FAILED"
>;

const connectedStatuses: Record<WhatsAppConnectedStatus, true> = {
  AUTH: true,
  AUTHENTICATED: true,
};

const disconnectedStatuses: Record<WhatsAppDisconnectedStatus, true> = {
  QR_FETCHED: true,
  FAILED: true,
};

function canUseSessionStorage() {
  return (
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined"
  );
}

export function isWhatsAppConnectedStatus(
  status: WhapiChannel["auth_status"] | undefined,
): status is WhatsAppConnectedStatus {
  if (!status) {
    return false;
  }

  return status in connectedStatuses;
}

export function isWhatsAppDisconnectedStatus(
  status: WhapiChannel["auth_status"] | undefined,
): status is WhatsAppDisconnectedStatus {
  if (!status) {
    return false;
  }

  return status in disconnectedStatuses;
}

export function getWhatsAppReconnectDismissKey(userId: number) {
  return `${WHATSAPP_RECONNECT_DISMISS_PREFIX}${userId}`;
}

export function getWhatsAppReconnectDismissed(userId: number) {
  if (!canUseSessionStorage()) {
    return false;
  }

  const key = getWhatsAppReconnectDismissKey(userId);
  return window.sessionStorage.getItem(key) === "true";
}

export function setWhatsAppReconnectDismissed(
  userId: number,
  dismissed: boolean,
) {
  if (!canUseSessionStorage()) {
    return;
  }

  const key = getWhatsAppReconnectDismissKey(userId);

  if (dismissed) {
    window.sessionStorage.setItem(key, "true");
    return;
  }

  window.sessionStorage.removeItem(key);
}

export function clearWhatsAppReconnectDismissed(userId?: number) {
  if (!canUseSessionStorage()) {
    return;
  }

  if (userId != null) {
    window.sessionStorage.removeItem(getWhatsAppReconnectDismissKey(userId));
    return;
  }

  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const sessionKey = window.sessionStorage.key(index);

    if (
      typeof sessionKey === "string" &&
      sessionKey.startsWith(WHATSAPP_RECONNECT_DISMISS_PREFIX)
    ) {
      window.sessionStorage.removeItem(sessionKey);
    }
  }
}
